// src/components/garden_draw/GardenDrawController.tsx
/**
 * Main controller component for the 'Suntap' garden drawing interface.
 * Coordinates between 3D viewer, drawing tools, and garden visualisation.
 * Acts as the parent component that manages the 3D scene and delegates
 * interactions to the appropriate subcomponents. Handles canvas click events,
 * garden selection, and provides methods for polygon drawing while maintaining
 * the relationship between the MultiMeshViewer and garden-specific functionality.
 */
import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { Box, Tooltip, IconButton, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import HeightIcon from '@mui/icons-material/Height';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { trackGardenEditStarted, trackGardenDeleted, trackUncertaintyToggled } from '../../utils/analytics';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import MultiMeshViewer, { MultiMeshViewerProps } from '../MultiMeshViewer';
import GardenDrawingTool, { GardenDrawingToolRef } from './GardenDrawingTool';
import GardenVisualisation from './GardenVisualisation';
import { Garden } from '../../types/gardenTypes';

// Define a base type without the properties we want to override or handle manually
type GardenDrawControllerPropsBase = Omit<MultiMeshViewerProps,
  | 'onCanvasClick'
  | 'onSceneReady'
  | 'onFocalPointChange'
  | 'onGardenSelect'
  | 'gardenLabels'
>;

// Define props for the controller component
export interface GardenDrawControllerProps extends GardenDrawControllerPropsBase {
  pubName?: string; // Name to display at the focal point
  onPolygonComplete?: (coordinates: [number, number, number][], gardenName?: string, isUncertain?: boolean) => void;
  onGardenDelete?: (gardenUid: string) => void;
  onToggleUncertainty?: (garden: Garden, isUncertain: boolean) => void;
  preservePolygon?: boolean;
  handleGardenNaming?: boolean;
  showInitialHelp?: boolean;
  googlePlaceId?: string;
  // This onGardenSelect is triggered when a VISUALIZED garden polygon is clicked
  onGardenSelect?: (garden: Garden) => void;
  onPubHasNoGarden?: () => void;
  pubHasNoGarden?: boolean;
  onGardensLoaded?: (gardens: Garden[]) => void;
  reload?: number;
}

interface ViewerObjects {
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    controls: OrbitControls;
    container: HTMLDivElement;
    trueUpVector: THREE.Vector3;
    localOrigin: THREE.Vector3;
}

/**
 * Main controller component that manages the 3D viewer and coordinates
 * between garden drawing and visualisation subcomponents
 */
const GardenDrawController: React.FC<GardenDrawControllerProps> = ({
  meshUris,
  initialZoom,
  focalPoint,
  focalPoints,
  pubName,
  height = '600px',
  onGardenSelect,
  onGardenDelete,
  onToggleUncertainty,
  onPolygonComplete,
  onPubHasNoGarden,
  onGardensLoaded,
  pubHasNoGarden = false,
  preservePolygon = false,
  handleGardenNaming = false,
  showInitialHelp = true,
  googlePlaceId,
  reload = 0,
  ...multiMeshProps
}) => {
  const drawingToolRef = useRef<GardenDrawingToolRef>(null);
  const [viewerObjects, setViewerObjects] = useState<ViewerObjects | null>(null);
  const [viewerIsReady, setViewerIsReady] = useState(false);
  const [selectedGardenForEdit, setSelectedGardenForEdit] = useState<Garden | null>(null);
  const [reloadGardens, setReloadGardens] = useState<number>(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showUncertaintyDialog, setShowUncertaintyDialog] = useState<boolean>(false);
  const [showPubHasGardenModal, setShowPubHasGardenModal] = useState<boolean>(false);
  const [gardenBeingAdjustedUid, setGardenBeingAdjustedUid] = useState<string | null>(null);
  const [gardenCount, setGardenCount] = useState<number>(0);

  // Memoize gardenLabels for the viewer from pubName and focalPoint
  const gardenLabels = useMemo(() => {
    if (pubName && focalPoint) {
      // The label will be placed at the centroid of these points. For a single label, it's just the point.
      return [{
        uid: 'main_pub_label',
        name: pubName,
        ecefCoordinates: [focalPoint]
      }];
    }
    return [];
  }, [pubName, focalPoint]);
  
  // Callback when meshes and scene are loaded in MultiMeshViewer
  const handleSceneReady = useCallback((data: { scene: THREE.Scene, camera: THREE.PerspectiveCamera, controls: OrbitControls, container: HTMLDivElement, localOrigin: THREE.Vector3 }) => {
    const trueUp = data.localOrigin.clone().normalize();
    setViewerObjects({
        scene: data.scene,
        camera: data.camera,
        controls: data.controls,
        container: data.container,
        trueUpVector: trueUp,
        localOrigin: data.localOrigin,
    });
    // Set camera on scene for GardenVisualisation's raycaster
    data.scene.userData.camera = data.camera;
    setViewerIsReady(true);
  }, []);

  const handleViewerCanvasClick = useCallback((event: MouseEvent, intersection: THREE.Intersection | null, raycaster: THREE.Raycaster) => {
    let isGardenVisualClick = false;
    if (intersection?.object) {
        let currentObject: THREE.Object3D | null = intersection.object;
        while(currentObject) {
            if (currentObject.name === 'GardensVisualisationGroup') {
                isGardenVisualClick = true;
                break;
            }
            currentObject = currentObject.parent;
        }
    }

    if (!isGardenVisualClick && drawingToolRef.current) {
        drawingToolRef.current.handleCanvasClick(event, intersection, raycaster);
    }
  }, []);

  useEffect(() => {
    if (pubHasNoGarden && googlePlaceId) {
      setShowPubHasGardenModal(true);
    }
  }, [pubHasNoGarden, googlePlaceId]);

  const handlePolygonComplete = useCallback((coordinates: [number, number, number][], gardenName?: string, isUncertain?: boolean) => {
    if (onPolygonComplete) {
      onPolygonComplete(coordinates, gardenName, isUncertain);
    }
    if (drawingToolRef.current?.clearVisuals) {
        setTimeout(() => {
            drawingToolRef.current?.clearVisuals();
        }, 100);
    }
    setGardenBeingAdjustedUid(null);
    setTimeout(() => {
      setReloadGardens(prev => prev + 1);
    }, 800);
  }, [onPolygonComplete]);

  const handleGardenSelectedFromVisualisation = useCallback((garden: Garden) => {
    if (drawingToolRef.current?.isBusy()) {
        return;
    }
    setSelectedGardenForEdit(garden);
    if (onGardenSelect) {
      onGardenSelect(garden);
    }
  }, [onGardenSelect]);

  const handleGardenDeselected = useCallback(() => {
    setSelectedGardenForEdit(null);
  }, []);

  const startNewDrawing = useCallback(() => {
    if (drawingToolRef.current) {
      setSelectedGardenForEdit(null);
      drawingToolRef.current.startDrawing?.();
    }
  }, []);
  
  const handleGardensLoaded = useCallback((gardens: Garden[]) => {
    setGardenCount(gardens.length);
    if (onGardensLoaded) {
      onGardensLoaded(gardens);
    }
  }, [onGardensLoaded]);

  const handleEditGardenClick = useCallback(() => {
    if (selectedGardenForEdit && drawingToolRef.current) {
      trackGardenEditStarted(googlePlaceId || '', gardenCount);
      drawingToolRef.current.startEditMode(selectedGardenForEdit);
      setSelectedGardenForEdit(null);
      handleGardenDeselected();
    }
  }, [selectedGardenForEdit, handleGardenDeselected, googlePlaceId, gardenCount]);

  const handleAdjustHeightClick = useCallback(() => {
    if (selectedGardenForEdit && drawingToolRef.current) {
        setGardenBeingAdjustedUid(selectedGardenForEdit.uid);
        drawingToolRef.current.startHeightAdjustment(selectedGardenForEdit);
        setSelectedGardenForEdit(null);
    }
  }, [selectedGardenForEdit]);

  const handleDrawingCancel = useCallback(() => {
    setGardenBeingAdjustedUid(null);
  }, []);

  const confirmDeleteGarden = useCallback(() => {
    if (selectedGardenForEdit && onGardenDelete) {
      trackGardenDeleted(googlePlaceId || '', selectedGardenForEdit.uid);
      onGardenDelete(selectedGardenForEdit.uid);
      setSelectedGardenForEdit(null);
      setTimeout(() => {
        setReloadGardens(prev => prev + 1);
      }, 800);
    }
    setShowDeleteDialog(false);
  }, [selectedGardenForEdit, onGardenDelete, googlePlaceId]);
  
  const handleUncertaintyClick = useCallback(() => {
    if (selectedGardenForEdit) {
      setShowUncertaintyDialog(true);
    }
  }, [selectedGardenForEdit]);
  
  const confirmToggleUncertainty = useCallback(() => {
    if (selectedGardenForEdit && onToggleUncertainty) {
      const newUncertaintyStatus = !selectedGardenForEdit.is_uncertain;
      trackUncertaintyToggled(selectedGardenForEdit.uid, newUncertaintyStatus);
      onToggleUncertainty(selectedGardenForEdit, newUncertaintyStatus);
      setTimeout(() => {
        setReloadGardens(prev => prev + 1);
      }, 800);
    }
    setShowUncertaintyDialog(false);
  }, [selectedGardenForEdit, onToggleUncertainty]);

  const focusOnPoint = (point: THREE.Vector3) => {
    if (viewerObjects) {
        viewerObjects.controls.target.copy(point);
        viewerObjects.controls.update();
    }
  };

  const enterPlanView = () => {
    if (viewerObjects) {
        const { camera, controls, trueUpVector } = viewerObjects;
        const currentDistance = camera.position.distanceTo(controls.target);
        camera.position.copy(controls.target).addScaledVector(trueUpVector, currentDistance);
        camera.up.copy(trueUpVector);
        camera.lookAt(controls.target);
        controls.minPolarAngle = 0;
        controls.maxPolarAngle = 0.0001;
        controls.enableRotate = false;
        controls.update();
    }
  };

  const exitPlanView = () => {
    if (viewerObjects) {
        const { controls } = viewerObjects;
        controls.minPolarAngle = 0;
        controls.maxPolarAngle = Math.PI;
        controls.enableRotate = true;
        controls.update();
    }
  };
  
  const { scene, container, controls, trueUpVector, localOrigin } = viewerObjects || {};
  const canRenderChildren = viewerIsReady && !!scene && !!container && !!controls && !!trueUpVector && !!localOrigin;

  return (
    <Box sx={{ position: 'relative', width: '100%', height }}>
      <MultiMeshViewer
        meshUris={meshUris}
        initialZoom={initialZoom}
        focalPoint={focalPoint}
        focalPoints={focalPoints}
        height="100%"
        onSceneReady={handleSceneReady}
        onCanvasClick={handleViewerCanvasClick}
        onFocalPointChange={() => setSelectedGardenForEdit(null)}
        onGardenSelect={() => setSelectedGardenForEdit(null)} // Handle focal point button clicks
        gardenLabels={gardenLabels} // Pass the constructed labels
        {...multiMeshProps}
      />

      {canRenderChildren && scene && container && controls && trueUpVector && localOrigin && (
        <>
          <GardenDrawingTool
            ref={drawingToolRef}
            scene={scene}
            container={container}
            controls={controls}
            trueUpVector={trueUpVector}
            localOrigin={localOrigin}
            onPolygonComplete={handlePolygonComplete}
            preservePolygon={preservePolygon}
            handleGardenNaming={handleGardenNaming}
            showInitialHelp={showInitialHelp}
            googlePlaceId={googlePlaceId}
            onFocusOnPoint={focusOnPoint}
            onEnterPlanView={enterPlanView}
            onExitPlanView={exitPlanView}
            onCancel={handleDrawingCancel}
          />
          <GardenVisualisation
            scene={scene}
            container={container}
            trueUpVector={trueUpVector}
            localOrigin={localOrigin}
            googlePlaceId={googlePlaceId}
            reload={reloadGardens}
            onGardenSelect={handleGardenSelectedFromVisualisation}
            onGardenDeselect={handleGardenDeselected}
            onGardensLoaded={handleGardensLoaded}
            gardenBeingAdjustedUid={gardenBeingAdjustedUid}
          />

           {selectedGardenForEdit ? (
              <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1010, pointerEvents: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Tooltip title={`Edit ${selectedGardenForEdit.name || 'Garden'}`}>
                      <IconButton color="primary" onClick={handleEditGardenClick} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' } }} size="medium" >
                          <EditIcon />
                      </IconButton>
                  </Tooltip>
                  <Tooltip title={`Adjust Height`}>
                      <IconButton color="primary" onClick={handleAdjustHeightClick} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' } }} size="medium" >
                          <HeightIcon />
                      </IconButton>
                  </Tooltip>
                  <Tooltip title={selectedGardenForEdit.is_uncertain ? `Mark as Certain` : `Mark as Uncertain`}>
                      <IconButton color={selectedGardenForEdit.is_uncertain ? "warning" : "primary"} onClick={handleUncertaintyClick} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' } }} size="medium" >
                          <HelpOutlineIcon />
                      </IconButton>
                  </Tooltip>
                  <Tooltip title={`Delete`}>
                  <IconButton color="error" onClick={() => setShowDeleteDialog(true)} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' } }} size="medium" >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
              </Box>
           ) : (
              !gardenBeingAdjustedUid && (
                <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1010, pointerEvents: 'auto' }}>
                    <Tooltip title="Draw New Area">
                        <IconButton color="primary" onClick={startNewDrawing} sx={{ backgroundColor: 'rgba(255, 255, 255, 1)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' } }} size="medium" >
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
              )
           )}
           
           {onPubHasNoGarden && gardenCount === 0 && (
             <Box sx={{ position: 'absolute', bottom: 10, right: 10, maxWidth: '50%', zIndex: 1010, pointerEvents: 'auto', textAlign: 'right' }}>
               <Typography variant="body2" component="span" onClick={onPubHasNoGarden} sx={{ color: 'rgba(100, 100, 100, 0.8)', fontStyle: 'italic', textDecoration: 'underline', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', backgroundColor: 'rgba(255, 255, 255, 0.6)', display: 'inline-block', wordWrap: 'break-word', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.8)', color: 'rgba(50, 50, 50, 0.9)' } }} >
                 {pubHasNoGarden ? "This pub has no outdoor space" : "Does this pub have no outdoor space?"}
               </Typography>
             </Box>
           )}

          <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} >
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to delete the garden "{selectedGardenForEdit?.name || 'Selected Garden'}"? This action cannot be undone.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
              <Button onClick={confirmDeleteGarden} color="error">Delete</Button>
            </DialogActions>
          </Dialog>
          
          <Dialog open={showUncertaintyDialog} onClose={() => setShowUncertaintyDialog(false)} >
            <DialogTitle> {selectedGardenForEdit?.is_uncertain ? "Mark as Certain" : "Mark as Uncertain"} </DialogTitle>
            <DialogContent>
              <Typography gutterBottom> {selectedGardenForEdit?.is_uncertain ? `Are you sure you want to mark the garden "${selectedGardenForEdit?.name || 'Selected Garden'}" as certain?` : `Are you sure you want to mark the garden "${selectedGardenForEdit?.name || 'Selected Garden'}" as uncertain?`} </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}> {selectedGardenForEdit?.is_uncertain ? "This indicates you're confident in the garden boundaries." : "This indicates you're not fully confident in the garden boundaries."} </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowUncertaintyDialog(false)}>Cancel</Button>
              <Button onClick={confirmToggleUncertainty} color={selectedGardenForEdit?.is_uncertain ? "primary" : "warning"}> {selectedGardenForEdit?.is_uncertain ? "Mark as Certain" : "Mark as Uncertain"} </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={showPubHasGardenModal} onClose={() => setShowPubHasGardenModal(false)} >
            <DialogTitle> This pub was marked as having no garden </DialogTitle>
            <DialogContent>
              <Typography gutterBottom> Someone previously marked this pub as having no outdoor space. If you believe this pub does have a garden or outdoor area, you can draw it using the tools on this page. </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}> Drawing a garden will automatically update the pub's status in our database. </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowPubHasGardenModal(false)} color="primary"> Got it </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

const GardenDraw3D = GardenDrawController;
export default GardenDraw3D;